/* eslint-disable import/no-default-export */
import React, {
  useState, useEffect, useCallback, useContext,
} from 'react';
import classNames from 'classnames';
import i18next from 'i18next';
import { stub as $t } from '@nbcnews/analytics-framework';

import Breakpoints from 'lib/Breakpoints';
import { getDateTime } from 'lib/DateTime';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { UNIVERSAL_CHECKOUT_ENABLED } from 'lib/brandFeatures';
import AIMS_FLAVORS from 'lib/aimsFlavors';
import buildAffiliateLink from 'lib/buildAffiliateLink';
import { getDataActivityMapID } from 'lib/articleUtils';
import { getAmazonUrlProductAsinAndAssociateTag } from 'lib/commerceUtils';
import { trackEcommerceShopClick } from 'lib/trackEcommerceShopClick';
import { ArticleContext } from 'lib/ContextTypes';

import Link from 'components/Link';
import { Picture } from 'components/Picture';
import { ProductTitle } from 'components/ProductTitle';
import ShopButtonInfo from 'components/ShopButtonInfo';
import { Save } from 'components/Social/SocialShareMenu/Save';
import { PromoCode } from 'components/PromoCode';
import { PictureCarousel } from 'components/PictureCarousel/index';
import { getProductOfferPricesAndAvailability } from 'lib/productOfferPriceHelpers';
import { PRODUCT } from 'lib/myNewsConstants';
import { withViewportVisibility } from 'lib/Hooks/withViewportVisibility';
import { Container } from './Container';
import { PromotionalMedia } from './PromotionalMedia';
import { Accordion } from '../Accordion';
import { ProsCons } from './ProsCons';
import { Flag } from './Flag';
import { Rating } from './Rating/index';
import { Badge } from './Badge';

import './styles.themed.scss';
import styles from './styles.module.scss';

const block = 'product';

const ENCODER_STOP_DATE = 'Wed Apr 15 2020 00:00:00';

$t('register', 'mbt_producttease_click', { allowDuplicate: true });
$t('register', 'mbt_spotlightode_click', { allowDuplicate: true });
$t('register', 'mbt_ecommerce_shop_button', { allowDuplicate: true });


/**
 * Renders a product component with various details and options.
 * @param {object} props - The properties passed to the component.
 * @param {string} [props.addClassNames=''] - Additional class names for styling.
 * @param {string} props.articlePublishDate - The publish date of the article.
 * @param {object} props.product - The product data.
 * @param {string} [props.size='medium'] - The size of the product component.
 * @param {string} props.vertical - The vertical category of the product.
 * @param {boolean} props.percentageOffIsVisible - Whether to show percentage off.
 * @param {string} props.detailsHeader - The header text for the details section.
 * @param {Function} props.htmlElement - Function to render HTML elements.
 * @param {string} props.hyphenId - The hyphenated ID for the product.
 */
const Product = ({
  addClassNames = '',
  articlePublishDate,
  product,
  size = 'medium',
  vertical,
  percentageOffIsVisible,
  detailsHeader,
  htmlElement,
  hyphenId,
}) => {
  const [currentMedia, setCurrentMedia] = useState(product?.teaseImage?.url?.primary);
  const [currSlideIdx, setCurrSlideIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const {
    content,
    id: productId,
    name,
    offers,
    productSpecifications,
    promoCode,
    promotionalMedia,
    prosAndCons,
    teaseImage,
  } = product;

  const {
    canonicalUrl,
  } = useContext(ArticleContext);

  useEffect(() => {
    setIsMobile(Breakpoints.isS());
    /**
     * Handle window resize events to update isMobile state
     */
    const handleResize = () => {
      setIsMobile(Breakpoints.isS());
    };

    window.addEventListener('resize', handleResize);
    const carouselEvent = size === 'large' ? 'mbt_spotlightode_action' : 'mbt_producttease_action';
    $t('register', carouselEvent, { allowDuplicate: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getProductLink = useCallback(
    (externalUrl) => {
      let productLink = externalUrl ?? product?.offers?.[0]?.externalUrl;

      if (!getFeatureConfigForBrand(UNIVERSAL_CHECKOUT_ENABLED, vertical)) {
        const fetchedEncoderStopDate = getDateTime(ENCODER_STOP_DATE);
        const fetchedCurrentArticlePublishDate = getDateTime(articlePublishDate);
        if (fetchedCurrentArticlePublishDate < fetchedEncoderStopDate) {
          productLink = buildAffiliateLink({ url: productLink, canonicalUrl });
        }
      }

      return productLink;
    },
    [articlePublishDate, canonicalUrl, product, vertical],
  );


  /**
   * Toggles the accordion state and tracks the event.
   */
  const onAccordionToggle = () => {
    const eventName = size === 'large' ? 'mbt_spotlightode_click' : 'mbt_producttease_click';
    $t('track', eventName, {
      action: isAccordionOpen ? 'what to know collapse' : 'what to know expand',
    });
    setIsAccordionOpen(!isAccordionOpen);
  };

  /**
   *
   * @param {object} media - Media object
   * @returns {JSX.Element}
   */
  const picture = (media) => {
    const flagName = product.taxonomy?.flag?.name;
    const badgeUrl = product.taxonomy?.badge?.logos[0]?.mediaAsset?.url?.primary || '';
    const badgeAlt = product.taxonomy?.badge?.name || '';
    const currentMediaWidth = product.teaseImage?.width;
    const currentMediaHeight = product.teaseImage?.height;

    const mediaUrl = media?.url?.primary || currentMedia;
    const mediaWidth = media?.width;
    const mediaHeight = media?.height;

    const isLarge = size === 'large';
    const isOde = isLarge;

    const sizes = isLarge
      ? {
        s: AIMS_FLAVORS.FOCAL_560x560,
      }
      : {
        s: AIMS_FLAVORS.FIT_320w,
        m: AIMS_FLAVORS.FIT_360w,
        l: AIMS_FLAVORS.FIT_260w,
        xl: AIMS_FLAVORS.FIT_260w,
      };
    const flavor = isLarge ? 'focal' : 'fit';

    const width = isLarge ? 560 : mediaWidth || currentMediaWidth;
    const height = isLarge ? 560 : mediaHeight || currentMediaHeight;

    const hasBadge = badgeUrl && badgeAlt;

    const pictureLinkClasses = classNames(
      `${block}__pictureLink`,
      'product__picture-link',
      {
        db: !mediaUrl,
        'db-m dn': !isMobile,
        'product__picture-link--isOde': isOde,
      },
    );

    const productLink = getProductLink();
    const { productAsin, associateTag } = getAmazonUrlProductAsinAndAssociateTag(productLink);

    return (
      <div className={pictureLinkClasses}>
        {flagName && <Flag text={flagName} />}
        {hasBadge && <Badge src={badgeUrl} alt={badgeAlt} />}
        <Link
          className="w-100"
          data-product-id={product.id}
          data-testid="product-picture-link"
          data-aps-asc-tag={associateTag}
          data-aps-asin={productAsin}
          href={productLink}
          onClick={() => trackEcommerceShopClick({ vertical, product })}
          rel="sponsored noopener noreferrer"
          target="_blank"
        >
          <Picture
            alt={product.teaseImage?.altText || product.name}
            flavor={flavor}
            lazyloaded
            originalHeight={height}
            originalWidth={width}
            responsiveFlavors={sizes}
            url={mediaUrl}
          />
        </Link>
        <Save
          contentId={product.id}
          contentType={PRODUCT}
          options={{ isThumbnail: true, showCTA: true }}
        />
      </div>
    );
  };


  /**
   * Renders a picture carousel for the product.
   * @returns {JSX.Element} The rendered picture carousel component.
   */
  const pictureCarousel = () => {
    const altText = teaseImage?.altText || name;

    const productLink = getProductLink();
    const mediaUrlOuter = promotionalMedia[0].url?.primary;
    const sizes = {
      s: AIMS_FLAVORS.FIT_320w,
      l: AIMS_FLAVORS.FIT_260w,
      xl: AIMS_FLAVORS.FIT_260w,
    };

    const pictureLinkClasses = classNames(
      `${block}__pictureLink`,
      'product__picture-link',
      {
        db: !mediaUrlOuter,
        'db-m dn': !isMobile,
      },
    );

    return (
      <PictureCarousel
        product={product}
        vertical={vertical}
        media={promotionalMedia}
        link={productLink}
        flagName={product?.taxonomy?.flag?.name}
        badgeUrl={product?.taxonomy?.badge?.logos[0]?.mediaAsset?.url?.primary}
        badgeAlt={product?.taxonomy?.badge?.name}
        additionalClasses={pictureLinkClasses}
        flavor="fit"
        size={size}
        sizes={sizes}
        altText={altText}
        contentId={productId}
        contentType={PRODUCT}
        onClick={() => trackEcommerceShopClick({ vertical, product })}
      />
    );
  };

  /**
   * Renders the offers for the product.
   * @returns {JSX.Element[]} Rendered offer elements.
   */
  const renderOffers = () => Array.isArray(offers)
  && offers.map((offer) => {
    if (!offer) return null;

    const vendorUrl = getProductLink(offer.externalUrl);
    const seller = getFeatureConfigForBrand(UNIVERSAL_CHECKOUT_ENABLED, vertical)
      ? 'Comprar'
      : offer.seller?.name;

    const { list, sale, priceDiscount } = getProductOfferPricesAndAvailability(offer);
    const priceDiscountPrefixText = priceDiscount?.prefixText || '';

    return (
      <ShopButtonInfo
        productId={product?.id}
        key={vendorUrl}
        additionalStyles={{
          vendor: 'product__vendor',
          cost: 'product__cost',
          shop: 'product__button',
        }}
        articlePublishDate={articlePublishDate}
        listPrice={list}
        name={product?.name}
        productUrl={vendorUrl}
        salePrice={sale}
        seller={seller}
        vertical={vertical}
        priceDiscountPrefixText={priceDiscountPrefixText}
        isProductUnit
        showPercentOff={percentageOffIsVisible}
      />
    );
  });


  /**
   * Renders the seller information for Universal Checkout.
   * @returns {JSX.Element|null} The seller information or null if not applicable.
   */
  const renderUniCheckoutSeller = () => (
    getFeatureConfigForBrand(UNIVERSAL_CHECKOUT_ENABLED, vertical) && offers?.[0]?.seller
      ? (
        <p className="product__seller-name">
            Vendido por&nbsp;
          {offers[0].seller.name}
        </p>
      )
      : null
  );

  /**
   * @param {object} specs - A product's productSpecifications object
   */
  const renderSpecs = (specs) => {
    const items = specs.map((s) => `<strong>${s.label}:</strong> ${s.description}`);
    return (
    // Using dangerouslySetInnerHTML here is okay because the data in
    // the "specs" property is coming from an NBCU-internal source,
    // supplied by our own editors
    /* eslint-disable-next-line react/no-danger */
      <div className="productSpecs" dangerouslySetInnerHTML={{ __html: items.join(' | ') }} />
    );
  };

  /**
   * Updates the current media and slide index.
   * @param {string} newMedia - The new media URL to set.
   * @param {number} newSlideIdx - The new slide index to set.
   */
  const updateMediaAndSlide = (newMedia, newSlideIdx) => {
    setCurrentMedia(newMedia);
    setCurrSlideIdx(newSlideIdx);
  };

  const affiliateLink = getProductLink();

  const { pros = {}, cons = {} } = prosAndCons || {};

  const isLarge = size === 'large';
  const hasPromotionalMedia = !!promotionalMedia?.length && isLarge;
  const hasProsCons = pros?.items?.length > 0;
  const hasSpecs = productSpecifications?.length > 0;
  const shouldRenderPictureCarousel = !!(promotionalMedia?.length > 1) && !isLarge;
  const shouldRenderProductDetails = !!content?.markupAndEmbeds?.length
        || hasProsCons
        || hasSpecs;

  return (
    <div
      id={productId}
      className={addClassNames}
      data-hyphen-id={hyphenId}
      data-testid="article-product-unit"
    >
      <ProductTitle
        affiliateLink={affiliateLink}
        onClick={() => trackEcommerceShopClick({ vertical, product })}
        productId={productId}
        productName={name}
      />
      <Container isLarge={isLarge} productName={name}>
        <div
          className={classNames('product__border', {
            'df flex-column flex-row-m': isLarge,
          })}
        >
          {hasPromotionalMedia && !isMobile && (
            <PromotionalMedia
              media={promotionalMedia}
              selectMedia={updateMediaAndSlide}
              selectedIdx={currSlideIdx}
              additionalStyles={{
                container:
                      'dn df-m flex-column w-4-m flex-grow-0 flex-shrink-0',
                item: 'mb2',
              }}
            />
          )}
          <div
            className={classNames(
              'df flex-column items-stretch product__card-container',
              {
                'flex-row-l flex-grow-1': isLarge,
                'flex-row-m': !isLarge,
              },
            )}
          >
            {shouldRenderPictureCarousel
              ? pictureCarousel(promotionalMedia)
              : picture()}
            <div
              className={classNames(
                `${block}__productCardInfo pt5 flex-grow-1`,
                { pb5: !hasProsCons && !isMobile },
                { 'pt0-m pb0-m': !isLarge },
                'product__card-info',
              )}
            >
              {hasPromotionalMedia && isMobile && (
                <PromotionalMedia
                  media={promotionalMedia}
                  selectMedia={updateMediaAndSlide}
                  selectedIdx={currSlideIdx}
                  isMobile={isMobile}
                  additionalStyles={{
                    container: 'dn-m df mb7',
                  }}
                />
              )}

              {renderUniCheckoutSeller(vertical, offers)}
              <Rating vertical={vertical} product={product} />
              {renderOffers()}
            </div>
          </div>
        </div>
        {promoCode && (
          <div className="product__promo-container">
            <PromoCode
              promoCode={promoCode.code}
              text={promoCode.suffixText}
              startsAt={promoCode.eventDateTime?.startsAt}
              endsAt={promoCode.eventDateTime?.endsAt}
            />
          </div>
        )}
        {shouldRenderProductDetails && (
          <Accordion
            additionalStyles={styles}
            title={i18next.t(detailsHeader || 'What to know')}
            suppressScroll
            onToggle={onAccordionToggle}
            isOpen={isAccordionOpen}
          >
            {content?.markupAndEmbeds?.map((bodyItem, index) => htmlElement(bodyItem, index))}
            {hasSpecs && renderSpecs(productSpecifications)}
            {hasProsCons && (
              <ProsCons
                pros={pros}
                cons={cons}
                isOde={isLarge}
                dataActivityMapId={getDataActivityMapID({
                  componentName: 'product',
                  pageRegion: 'article-body',
                  componentTitle: name,
                })}
              />
            )}
          </Accordion>
        )}
      </Container>
    </div>
  );
};

export default Product;

/**
 * @param {object} props - Component props
 * @param {boolean} shouldShowComponent - Should show component
 * @param {string} defaultId - Default ID
 */
export const getIdForLazyProduct = (props, shouldShowComponent, defaultId) => {
  const {
    product: { id },
  } = props;
  return shouldShowComponent ? defaultId || id : undefined;
};


export const LazyProduct = withViewportVisibility(
  Product,
  undefined,
  getIdForLazyProduct,
);
